import { useState } from 'react';
import { CloseSquare, Edit2, Trash } from 'iconsax-react';
import InfoText from '../../../../components/atoms/InfoText';
import { SubCategory } from '../../../../types/lookups';
import { useTranslation } from 'react-i18next';
import { useDeleteOperatorSpecialityMutation } from '../../../../store/services/lookups';
import { message } from 'antd';
import CustomModal from '../../../../components/atoms/CustomModal';
import TitleText from '../../../../components/atoms/TitleText';
import Button from '../../../../components/atoms/Button';

interface SubCategoryRowProps {
  subCategory: SubCategory;
  onEdit: () => void;
}

export default function SubCategoryRow({
  subCategory,
  onEdit,
}: SubCategoryRowProps) {
  const { t } = useTranslation();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteOperatorSpeciality, { isLoading: isDeleting }] = useDeleteOperatorSpecialityMutation();

  return (
    <>
      <div className="flex items-center justify-between py-2.5 border-b border-gray-100 last:border-0 ps-10 pe-4 bg-gray-50/50">
        <div className="flex items-center gap-3 ms-4">
          <img
            src={subCategory.blobUrl || 'https://via.placeholder.com/40'}
            alt={subCategory.nameEn}
            className="w-10 h-10 rounded-lg object-cover border border-gray-200"
          />

          <InfoText
            text={`${subCategory.nameEn} | ${subCategory.nameAr}`}
            className="!text-gray-700 text-sm font-medium"
          />
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={onEdit}
            className="w-8 h-8 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-600 flex items-center justify-center transition-colors"
          >
            <Edit2 size={16} variant="Bold" />
          </button>
          <button
            onClick={() => setIsDeleteModalOpen(true)}
            className="w-8 h-8 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 flex items-center justify-center transition-colors"
          >
            <Trash size={16} variant="Bold" />
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <CustomModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title={null}
        centered
        width={715}
        closable={false}
      >
        <div className="flex flex-col justify-center text-center gap-2 py-4">
          <div className="w-12 h-12 rounded-[14px] bg-[#FEF2F2] flex items-center justify-center self-center mb-2">
            <CloseSquare
              size={24}
              className="text-[#EF4444]"
              variant="Outline"
            />
          </div>

          <TitleText
            text={t('home.delete-item-title')}
            size="2xl"
            className="!font-bold !text-[#111928]"
          />

          <p className="text-gray-500 text-sm max-w-[420px] mx-auto leading-relaxed">
            {t('home.delete-item-desc')}
          </p>

          <div className="flex flex-col-reverse sm:flex-row items-center justify-center sm:justify-end gap-3 mt-6 w-full">
            <Button
              variant="outline"
              onClick={() => setIsDeleteModalOpen(false)}
              className="w-full sm:w-auto rounded-xl px-8 py-2 font-semibold border border-gray-300 !bg-white !text-[#111928] hover:!bg-gray-50"
            >
              {t('home.cancel')}
            </Button>
            <Button
              variant="dark"
              loading={isDeleting}
              disabled={isDeleting}
              onClick={async () => {
                try {
                  await deleteOperatorSpeciality(subCategory.id).unwrap();
                  message.success(t('categories.subCategoryDeletedSuccessfully', 'Sub-category deleted successfully'));
                  setIsDeleteModalOpen(false);
                } catch (error: any) {
                  console.error('Failed to delete sub-category:', error);
                  const errorMessage =
                    error?.data?.message ||
                    error?.data?.errors?.default ||
                    error?.data?.errors?.[0] ||
                    t('categories.subCategoryDeleteFailed', 'Failed to delete sub-category');
                  message.error(errorMessage);
                }
              }}
              className="w-full sm:w-auto rounded-xl px-12 py-2 font-semibold !bg-[#D92D20] hover:!bg-[#B91C1C] !text-white !border-[#D92D20]"
            >
              {t('home.yes-delete')}
            </Button>
          </div>
        </div>
      </CustomModal>
    </>
  );
}
